import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { Helmet } from "react-helmet";
import About from "./About";
import omorelogo from "../Images/omorelogo.png";

export default function SynchroIntro() {
  const navigate = useNavigate();
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [isPressing, setIsPressing] = useState(false);

  // Loader logic: Specific key for Synchro
  const [isLoading, setIsLoading] = useState(() => {
    return !sessionStorage.getItem("hasLoaded_synchro");
  });
  
  const timerRef = useRef(null);
  const HOLD_DURATION = 800;

  useEffect(() => {
    if (isLoading && isFullscreen) {
      const loadTimer = setTimeout(() => {
        setIsLoading(false);
        sessionStorage.setItem("hasLoaded_synchro", "true");
      }, 2500);
      return () => clearTimeout(loadTimer);
    }
  }, [isLoading, isFullscreen]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [isFullscreen]);

  const handlePressStart = (e) => {
    if (e.type === 'touchstart') e.preventDefault();
    setIsPressing(true);
    timerRef.current = setTimeout(() => {
      setIsFullscreen((prev) => !prev);
      setIsPressing(false);
      if (!isFullscreen) setIsLoading(true);
    }, HOLD_DURATION);
  };

  const handlePressEnd = () => {
    setIsPressing(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  };

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>SYNCHRO – Rhythm & Beat Matcher Test | OMORE Games</title>
        <meta name="description" content="Test your internal clock with SYNCHRO. A high-fidelity rhythm game designed to measure your ability to stay in sync with complex patterns." />
        <meta name="keywords" content="synchro game, rhythm game online, beat matcher, timing test, music reflex game, omore games, brain training" />
        <meta name="theme-color" content="#0d0a12" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoGame",
            "name": "SYNCHRO",
            "genre": "Rhythm Game",
            "author": { "@type": "Person", "name": "Omkar More" },
            "description": "Rhythm and beat matching challenge for cognitive synchronization."
          })}
        </script>
      </Helmet>

      <div className={isFullscreen
        ? "fixed inset-0 z-50 bg-[#0a0a0b] overflow-hidden flex flex-col select-none"
        : "relative w-full bg-transparent"
      }>

        {/* --- TIER 0: CINEMATIC STUDIO LOADER --- */}
        {isFullscreen && isLoading && (
          <div className="absolute inset-0 z-[200] bg-gradient-to-b from-[#0a0a0b] to-[#0b0a0d] flex flex-col items-center justify-center transition-opacity duration-1000">
            {/* Purple Background Glow for SYNCHRO */}
            <div className="absolute w-[60vw] h-[60vw] bg-purple-600/5 blur-[100px] rounded-full opacity-70" />

            <div className="flex flex-col items-center space-y-8 animate-in fade-in zoom-in-95 duration-1000">
              <div className="relative">
                <img
                  src={omorelogo}
                  alt="OMORE Logo"
                  className="h-20 md:h-32 w-auto brightness-110 drop-shadow-[0_0_25px_rgba(168,85,247,0.25)]"
                />
                <div className="absolute inset-0 bg-purple-500/20 blur-xl opacity-70 animate-pulse rounded-full" />
              </div>

              <div className="text-center space-y-4">
                <h2 className="text-sm md:text-xl text-white tracking-[0.9em] md:tracking-[1.4em] uppercase font-black -mr-[0.9em] md:-mr-[1.4em]">
                  OMORE GAMES
                </h2>
                <div className="flex items-center justify-center gap-4">
                  <div className="h-[2px] w-10 bg-zinc-800" />
                  <span className="text-[12px] md:text-sm text-zinc-300 tracking-[0.6em] uppercase font-bold -mr-[0.6em]">presents</span>
                  <div className="h-[2px] w-10 bg-zinc-800" />
                </div>
              </div>
            </div>

            <div className="absolute bottom-12 flex flex-col items-center gap-1 opacity-50 select-none">
              <span className="text-[8px] tracking-[0.7em] text-zinc-500 uppercase">Created by</span>
              <span className="text-[11px] tracking-[0.9em] text-white/80 uppercase font-semibold -mr-[0.9em]">Omkar More</span>
            </div>
          </div>
        )}

        {/* --- CINEMATIC OVERLAYS (SYNCHRO PURPLE) --- */}
        {isFullscreen && (
          <>
            <div className="absolute inset-0 pointer-events-none opacity-20 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(168,85,247,0.06),rgba(168,85,247,0.02),rgba(168,85,247,0.06))] z-50 bg-[length:100%_2px,3px_100%]" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] bg-purple-600/5 blur-[120px] rounded-full pointer-events-none" />
          </>
        )}

        <div className={`
          relative flex flex-col items-center w-full max-w-7xl mx-auto px-6 text-center text-white transition-opacity duration-1000
          ${isFullscreen ? "h-[100svh] justify-between py-10 md:py-16" : "min-h-[85svh] justify-center py-12"}
          ${isLoading && isFullscreen ? "opacity-0" : "opacity-100"}
        `}>

          {/* TOP: Identity */}
          <div className="w-full flex flex-col items-center shrink-0">
            <div className="flex flex-col items-center gap-2">
              <img src={omorelogo} alt="Logo" className="h-10 md:h-12 w-auto opacity-80" />
              <div className="px-4 py-1 border border-purple-500/30 bg-purple-500/5 rounded-full mt-2">
                <p className="text-[8px] md:text-[10px] text-purple-400 tracking-[0.5em] uppercase font-black">
                  Frequency Synchronized
                </p>
              </div>
            </div>
          </div>

          {/* CENTER: Game Info */}
          <div className="flex flex-col items-center justify-center flex-grow w-full relative py-8">
            {isFullscreen && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
                <span className="opacity-[0.02] font-black text-[30vw] uppercase italic leading-none text-purple-500">SYNC</span>
              </div>
            )}

            <div className="mb-8 md:mb-12 z-10">
              <h1 className="text-7xl sm:text-8xl md:text-[10rem] font-black tracking-tighter leading-none text-white drop-shadow-[0_0_40px_rgba(168,85,247,0.4)]">
                SYNCHRO
              </h1>
              <div className="flex items-center justify-center gap-4 mt-2">
                <div className="h-[1px] w-12 bg-gradient-to-r from-transparent to-purple-500" />
                <span className="text-[10px] md:text-xs tracking-[1em] uppercase text-purple-400 font-bold ml-[1em]">Rhythm</span>
                <div className="h-[1px] w-12 bg-gradient-to-l from-transparent to-purple-500" />
              </div>
            </div>

            <div className="flex flex-col items-center space-y-8 z-10">
              <div className="space-y-3">
                <h2 className="text-2xl md:text-5xl font-extralight tracking-[0.4em] uppercase text-white/90">
                  Beat Matcher
                </h2>
                <p className="text-[9px] md:text-xs text-zinc-500 tracking-[0.6em] uppercase">Stay in time with the signals.</p>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 pt-4 w-full">
                <button
                  disabled
                  className="group relative w-full max-w-[280px] md:w-auto px-16 py-5 md:px-24 md:py-7 bg-zinc-800 text-zinc-500 text-xs md:text-sm uppercase font-black cursor-not-allowed"
                >
                  <span className="relative z-10 tracking-[1.2em] -mr-[1.2em]">In Planning</span>
                </button>

                <button
                  onMouseDown={handlePressStart} onMouseUp={handlePressEnd}
                  onTouchStart={handlePressStart} onTouchEnd={handlePressEnd}
                  className="group relative w-full max-w-[280px] md:w-auto px-10 py-5 md:py-7 border border-white/10 bg-white/5 backdrop-blur-sm"
                >
                  <span className="relative z-10 text-[9px] md:text-[10px] tracking-[0.4em] uppercase font-bold text-zinc-500 group-hover:text-zinc-300">
                    {isPressing ? "Processing..." : isFullscreen ? "Hold to Exit" : "Hold for Fullscreen"}
                  </span>
                  <div className="absolute bottom-0 left-0 w-full h-[2px] bg-white/5">
                    <div 
                      className="h-full bg-purple-600 shadow-[0_0_10px_#a855f7] transition-all ease-linear"
                      style={{ width: isPressing ? "100%" : "0%", transitionDuration: isPressing ? `${HOLD_DURATION}ms` : "150ms" }}
                    />
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* BOTTOM: Stats */}
          <div className="w-full flex flex-col items-center shrink-0 space-y-6">
            <div className="text-[9px] md:text-xs text-zinc-400 tracking-[0.5em] md:tracking-[0.8em] uppercase font-black flex items-center justify-center gap-4 md:gap-8 opacity-40">
              <span>BPM</span>
              <span className="text-purple-500">•</span>
              <span>Flow</span>
              <span className="text-purple-500">•</span>
              <span>Timing</span>
            </div>
            
            {isFullscreen && (
              <div className="flex items-center justify-center gap-6 md:gap-12 text-[8px] text-zinc-600 tracking-[0.4em] uppercase font-bold">
                <div className="flex flex-col items-center gap-1">
                  <span className="text-purple-500/40">Clock</span>
                  <span>Internal</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-purple-500/40">Buffer</span>
                  <span>Adaptive</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-purple-500/40">Signal</span>
                  <span>Steady</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {!isFullscreen && <About />}
    </>
  );
}